/**
 * Generic, engine-driven progress rail for a workflow instance.
 *
 * A ``WorkflowStateBadge`` tells you *where* an instance is; this rail
 * tells you *how far along* it is. It is deliberately resource-agnostic:
 * it consumes the ordered ``states`` and directed ``transitions`` the
 * instance-detail API exposes (see ``WorkflowInstanceSerializer``), walks
 * the graph to find the linear "spine", and collapses every post-fork
 * branch/outcome state into a single trailing outcome node. Nothing here
 * knows about inspections, claims, or any tenant - a claim lifecycle and
 * an inspection lifecycle render through the exact same component.
 *
 * Algorithm:
 *   1. Build adjacency from ``transitions`` (source_key -> [target_key]).
 *   2. Start at the ``is_initial`` state and follow the single outgoing
 *      edge at each step. The walk stops at the first *fork* (a state
 *      with more than one distinct outgoing target) or dead end - that
 *      fork node is the last spine node.
 *   3. Any state not on the spine is an "outcome" (passed / repair_pending
 *      / failed / closed / cancelled …). These collapse into one trailing
 *      node so the rail stays a clean left-to-right progression rather
 *      than exploding into every branch.
 *   4. Status per node:
 *        - current state is ON the spine  -> nodes before it are ``done``
 *          (or ``skipped``, see below), it is ``current``, nodes after are
 *          ``upcoming``; the outcome node (if any) is ``upcoming``.
 *        - current state is an OUTCOME     -> every spine node is ``done``
 *          (or ``skipped``) and the outcome node is ``current`` (rendered
 *          with the current state's own advisory colour + label).
 *
 * A node ordered *before* the current one is only ``done`` when the instance
 * ACTUALLY visited it. When ``visitedStateKeys`` is supplied (derived from the
 * action-log history, see ``computeVisitedStateKeys``) and a prior node's key
 * is absent from it, the node is ``skipped`` instead - the instance took a
 * shortcut past it (e.g. a bulk-settle jump). Skipped nodes render distinctly
 * (dashed, muted, no check) so the rail never claims a stage the claim never
 * passed through. Without ``visitedStateKeys`` the rail keeps its historical
 * position-based behaviour (every prior node ``done``).
 *
 * Degenerate fallback: if the graph carries no usable edges (older data),
 * the rail renders every state ordered by ``order`` with the current one
 * highlighted - no fork/outcome collapsing.
 */

import { Check, Minus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import type {
  WorkflowState,
  WorkflowTransitionEdge,
} from '@/shared/lib/workflows/types'

import { useWorkflowStateLabel } from './use-workflow-i18n'

type NodeStatus = 'done' | 'current' | 'upcoming' | 'skipped'

interface RailNode {
  /** Stable state key, or ``__outcome__`` for the collapsed outcome node. */
  key: string
  /** State key used for i18n label resolution (null for a generic node). */
  stateKey: string | null
  label: string
  color: string | null
  status: NodeStatus
}

const OUTCOME_KEY = '__outcome__'

export interface WorkflowProgressRailProps {
  states: WorkflowState[] | undefined
  transitions: WorkflowTransitionEdge[] | undefined
  currentStateKey: string | undefined
  /**
   * Keys of the states the instance actually visited (from the action-log
   * history - see ``computeVisitedStateKeys``). When supplied, a node ordered
   * before the current one that is NOT in this set renders as ``skipped``
   * rather than ``done``. When omitted, every prior node renders ``done``
   * (historical position-based behaviour).
   */
  visitedStateKeys?: ReadonlySet<string>
  /** Optional workflow key for tenant-specific label translation. */
  workflowKey?: string
  className?: string
}

/**
 * Walk the directed graph from the initial state, following the single
 * outgoing edge at each step, and return the ordered spine of state keys.
 * The walk stops at the first fork (multiple distinct targets) or dead
 * end; the fork node itself is included as the final spine node.
 *
 * Edges that target a *terminal* state (``is_terminal``) are ignored when
 * walking the spine. Cancel / abort transitions to a terminal end-state
 * (e.g. ``cancelled``) hang off nearly every state, so counting them as
 * forks would truncate the spine at the initial state. They are escape
 * hatches, not part of the forward progression; the genuine outcome fork
 * (``in_progress`` -> passed / repair_pending / failed / …) branches to
 * *non-terminal* states and is still detected.
 */
function computeSpine(
  states: WorkflowState[],
  transitions: WorkflowTransitionEdge[],
): string[] {
  const byKey = new Map(states.map((s) => [s.key, s]))

  // Adjacency: source_key -> distinct target keys, ordered by edge order.
  // Skip edges into terminal states so cancel/abort escape hatches don't
  // read as forks.
  const adjacency = new Map<string, string[]>()
  const seenEdge = new Set<string>()
  const sortedEdges = [...transitions].sort((a, b) => a.order - b.order)
  for (const edge of sortedEdges) {
    if (!edge.source_key || !edge.target_key) continue
    if (edge.source_key === edge.target_key) continue
    if (!byKey.has(edge.source_key) || !byKey.has(edge.target_key)) continue
    if (byKey.get(edge.target_key)?.is_terminal) continue
    const dedupe = `${edge.source_key}->${edge.target_key}`
    if (seenEdge.has(dedupe)) continue
    seenEdge.add(dedupe)
    const list = adjacency.get(edge.source_key) ?? []
    list.push(edge.target_key)
    adjacency.set(edge.source_key, list)
  }

  const initial =
    states.find((s) => s.is_initial) ??
    [...states].sort((a, b) => a.order - b.order)[0]
  if (!initial) return []

  const spine: string[] = [initial.key]
  const visited = new Set<string>([initial.key])
  let cursor = initial.key
  // Follow the single outgoing edge until a fork / dead end / cycle.
  for (;;) {
    const targets = (adjacency.get(cursor) ?? []).filter(
      (t) => !visited.has(t),
    )
    if (targets.length !== 1) break
    const next = targets[0]
    spine.push(next)
    visited.add(next)
    cursor = next
  }
  return spine
}

/**
 * Status of a node ordered *before* the current one: ``done`` when the
 * instance visited it, ``skipped`` when a visited-set is known and it is
 * absent from it. With no visited-set, prior nodes stay ``done`` (historical
 * position-based behaviour).
 */
function priorStatus(
  key: string,
  visited: ReadonlySet<string> | undefined,
): NodeStatus {
  if (!visited) return 'done'
  return visited.has(key) ? 'done' : 'skipped'
}

function buildNodes(
  states: WorkflowState[],
  transitions: WorkflowTransitionEdge[],
  currentStateKey: string | undefined,
  visitedStateKeys: ReadonlySet<string> | undefined,
): RailNode[] {
  const byKey = new Map(states.map((s) => [s.key, s]))
  const spineKeys = computeSpine(states, transitions)

  // Degenerate: no usable spine (no edges) -> linear by order, no outcome.
  if (spineKeys.length <= 1 && transitions.length === 0) {
    const ordered = [...states].sort((a, b) => a.order - b.order)
    const currentIdx = ordered.findIndex((s) => s.key === currentStateKey)
    return ordered.map((s, i) => ({
      key: s.key,
      stateKey: s.key,
      label: s.label,
      color: s.color,
      status:
        currentIdx < 0
          ? 'upcoming'
          : i < currentIdx
            ? priorStatus(s.key, visitedStateKeys)
            : i === currentIdx
              ? 'current'
              : 'upcoming',
    }))
  }

  const spineSet = new Set(spineKeys)
  const currentOnSpine =
    currentStateKey != null && spineSet.has(currentStateKey)
  const currentSpineIdx = currentOnSpine
    ? spineKeys.indexOf(currentStateKey as string)
    : -1

  const spineNodes: RailNode[] = spineKeys.map((key, i) => {
    const state = byKey.get(key)
    let status: NodeStatus
    if (!currentOnSpine) {
      // Current is an outcome -> the whole spine is behind us; each node is
      // ``done`` only if the instance actually visited it, else ``skipped``.
      status = priorStatus(key, visitedStateKeys)
    } else if (i < currentSpineIdx) {
      status = priorStatus(key, visitedStateKeys)
    } else if (i === currentSpineIdx) {
      status = 'current'
    } else {
      status = 'upcoming'
    }
    return {
      key,
      stateKey: key,
      label: state?.label ?? key,
      color: state?.color ?? null,
      status,
    }
  })

  // Any state not on the spine is an outcome; collapse to one trailing node.
  const hasOutcomeStates = states.some((s) => !spineSet.has(s.key))
  if (!hasOutcomeStates) return spineNodes

  const currentOutcome =
    !currentOnSpine && currentStateKey != null
      ? byKey.get(currentStateKey)
      : undefined

  const outcomeNode: RailNode = currentOutcome
    ? {
        key: OUTCOME_KEY,
        stateKey: currentOutcome.key,
        label: currentOutcome.label,
        color: currentOutcome.color,
        status: 'current',
      }
    : {
        // Generic placeholder - the concrete outcome is not known until
        // the fork resolves. Label is resolved via i18n in RailNodeItem.
        key: OUTCOME_KEY,
        stateKey: null,
        label: '',
        color: null,
        status: 'upcoming',
      }

  return [...spineNodes, outcomeNode]
}

/**
 * A single rail node. Split into its own component so the i18n hook can
 * be called once per node without violating the rules of hooks.
 */
function RailNodeItem({
  node,
  index,
  isLast,
  workflowKey,
}: {
  node: RailNode
  index: number
  isLast: boolean
  workflowKey?: string
}) {
  const { t } = useTranslation()
  // Generic outcome placeholder carries no state key - resolve a
  // translated generic label; concrete states resolve key-first.
  const stateLabel = useWorkflowStateLabel(
    node.stateKey ?? undefined,
    node.label,
    workflowKey,
  )
  const resolvedLabel = node.stateKey
    ? stateLabel
    : t('workflows.progress.outcome', { defaultValue: 'Outcome' })

  const isDone = node.status === 'done'
  const isCurrent = node.status === 'current'
  const isSkipped = node.status === 'skipped'

  // A skipped node is a stage the instance never entered (a shortcut jumped
  // past it). It must NOT read as completed - no fill, no check - so it is
  // rendered as a hollow, dashed, muted circle with a minus glyph.
  const skippedLabel = isSkipped
    ? t('workflows.progress.skipped', { defaultValue: 'Skipped' })
    : undefined

  // The current outcome node is tinted with its own advisory colour to
  // match ``WorkflowStateBadge``; spine nodes use neutral tokens.
  const currentColorStyle =
    isCurrent && node.color
      ? { backgroundColor: node.color, borderColor: node.color }
      : undefined

  return (
    <li className="flex min-w-0 flex-1 items-center gap-0">
      <div className="flex min-w-0 flex-col items-center gap-1.5">
        <span
          title={skippedLabel}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-medium',
            isDone && 'bg-[var(--ink)] text-[var(--bg-card)]',
            isCurrent &&
              !node.color &&
              'border-[1.5px] border-[var(--ink)] bg-[var(--bg-card)] text-[var(--ink)]',
            isCurrent && node.color && 'text-[var(--bg-card)]',
            isSkipped &&
              'border border-dashed border-[var(--rule)] bg-transparent text-[var(--ink-3)] opacity-60',
            !isDone &&
              !isCurrent &&
              !isSkipped &&
              'border border-[var(--rule)] bg-[var(--bg-card)] text-[var(--ink-3)]',
          )}
          style={currentColorStyle}
        >
          {isDone ? (
            <Check size={13} strokeWidth={2.5} />
          ) : isSkipped ? (
            <Minus size={13} strokeWidth={2.5} />
          ) : (
            index + 1
          )}
        </span>
        <span
          className={cn(
            'max-w-[88px] text-center text-[10px] uppercase leading-tight tracking-[0.06em]',
            isCurrent
              ? 'font-medium text-[var(--ink)]'
              : isSkipped
                ? 'text-[var(--ink-3)] line-through opacity-60'
                : 'text-[var(--ink-3)]',
          )}
        >
          {resolvedLabel}
        </span>
      </div>
      {!isLast && (
        <span
          aria-hidden
          className={cn(
            'mx-1 mb-5 h-px min-w-4 flex-1',
            isDone ? 'bg-[var(--ink)]' : 'bg-[var(--rule)]',
          )}
        />
      )}
    </li>
  )
}

export function WorkflowProgressRail({
  states,
  transitions,
  currentStateKey,
  visitedStateKeys,
  workflowKey,
  className,
}: WorkflowProgressRailProps) {
  if (!states || states.length === 0) return null

  const nodes = buildNodes(
    states,
    transitions ?? [],
    currentStateKey,
    visitedStateKeys,
  )
  if (nodes.length === 0) return null

  return (
    <ol
      className={cn(
        'flex items-start gap-0 overflow-x-auto pb-1',
        className,
      )}
    >
      {nodes.map((node, i) => (
        <RailNodeItem
          key={node.key}
          node={node}
          index={i}
          isLast={i === nodes.length - 1}
          workflowKey={workflowKey}
        />
      ))}
    </ol>
  )
}
